// components/CreatePhoneSystemModal.jsx
import React, { useState } from "react";
import { X, PhoneCall, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreatePhoneSystemModal = ({ accountId, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    systemType: "",
    licenses: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const systemTypes = [
    "NEC on Prem",
    "Samsung on Prem",
    "IPECS",
    "Callswitch Legacy",
    "Callswitch One",
    "Webex",
    "Luminate",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.systemType) {
      setError("Please select a system type");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiService.createPhoneSystem(accountId, {
        ...formData,
        licenses: formData.licenses ? parseInt(formData.licenses) : null,
      });
      onSuccess();
    } catch (err) {
      console.error("Error creating phone system:", err);
      setError(err.response?.data?.message || "Failed to create phone system");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg border max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div
          className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-blue-600/10" : "bg-blue-100"
              }`}
            >
              <PhoneCall
                className={isDarkMode ? "text-blue-400" : "text-blue-600"}
                size={20}
              />
            </div>
            <h2
              className={`text-xl font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Add Phone System
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div
              className={`border rounded-lg p-4 text-sm ${
                isDarkMode
                  ? "bg-red-500/10 border-red-500/20 text-red-400"
                  : "bg-red-50 border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          {/* System Type */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              System Type *
            </label>
            <select
              value={formData.systemType}
              onChange={(e) =>
                setFormData({ ...formData, systemType: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
              required
            >
              <option value="">Select system type...</option>
              {systemTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Licenses */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Number of Licenses
            </label>
            <input
              type="number"
              min="0"
              value={formData.licenses}
              onChange={(e) =>
                setFormData({ ...formData, licenses: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
              placeholder="Enter number of licenses"
            />
          </div>

          {/* Notes */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
              placeholder="Additional notes about this phone system..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-6 py-2.5 rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              }`}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Phone System"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePhoneSystemModal;
